import {union} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {
  AutomaticMessageSettingsValue,
  EmailContextTemplateValue,
  SmsContextTemplateValue,
} from '../types/AutomaticMessageSettingsValue';
import {ContextTemplateRecords} from '../types/FormValues';

const DEFAULT_BODY_FORMAT = 'text';

const trimmed = (value: string | null | undefined): string => value?.trim() ?? '';

/**
 * A locale is emitted only once it carries content, so an untouched locale does not
 * create an empty template. `bodyFormat` is carried over from the stored value because
 * the form does not edit it — rebuilding it would downgrade an HTML template to text.
 */
export const buildContextTemplatesRequest = (
  records: ContextTemplateRecords,
  storedValue: AutomaticMessageSettingsValue | undefined
): Pick<AutomaticMessageSettingsValue, 'emailContextTemplates' | 'smsContextTemplates'> => {
  const storedBodyFormatByLocale = new Map<string, EmailContextTemplateValue['bodyFormat']>(
    (storedValue?.emailContextTemplates ?? []).map((item) => [item.locale, item.bodyFormat])
  );

  const emailLocales = union(Object.keys(records.emailSubjects), Object.keys(records.emailBodies));

  const emailContextTemplates = emailLocales.reduce<EmailContextTemplateValue[]>(
    (accumulator, locale) => {
      const subject = trimmed(records.emailSubjects[locale]);
      const body = trimmed(records.emailBodies[locale]);

      if (isNilOrEmpty(subject) && isNilOrEmpty(body)) {
        return accumulator;
      }

      accumulator.push({
        locale,
        subject,
        body,
        bodyFormat: storedBodyFormatByLocale.get(locale) ?? DEFAULT_BODY_FORMAT,
      });

      return accumulator;
    },
    []
  );

  const smsContextTemplates = Object.keys(records.smsBodies).reduce<SmsContextTemplateValue[]>(
    (accumulator, locale) => {
      const body = trimmed(records.smsBodies[locale]);

      if (isNilOrEmpty(body)) {
        return accumulator;
      }

      accumulator.push({locale, body});

      return accumulator;
    },
    []
  );

  return {emailContextTemplates, smsContextTemplates};
};
